<script setup>
import { ref, computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useEmitter } from 'dashboard/composables/emitter';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';

import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import wootConstants from 'dashboard/constants/globals';
import {
  CMD_REOPEN_CONVERSATION,
  CMD_RESOLVE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const arrowDownButtonRef = ref(null);
const isLoading = ref(false);

const [showActionsDropdown, toggleDropdown] = useToggle();
const closeDropdown = () => toggleDropdown(false);
const openDropdown = () => toggleDropdown(true);

const currentChat = computed(() => getters.getSelectedChat.value);

const isOpen = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.OPEN
);
const isPending = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.PENDING
);
const isResolved = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.RESOLVED
);
const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);
const isClosed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.CLOSED
);
const isShowedInterest = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SHOWED_INTEREST
);
const isRejected = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.REJECTED
);
const isAbandoned = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.ABANDONED
);
const isVisitedStore = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.VISITED_STORE
);

const showReopenButton = computed(() => {
  return isResolved.value || isPending.value || isSnoozed.value || isClosed.value || isRejected.value || isAbandoned.value;
});

const getConversationParams = () => {
  const allConversations = document.querySelectorAll(
    '.conversations-list .conversation'
  );

  const activeConversation = document.querySelector(
    'div.conversations-list div.conversation.active'
  );
  const activeConversationIndex = [...allConversations].indexOf(
    activeConversation
  );
  const lastConversationIndex = allConversations.length - 1;

  return {
    all: allConversations,
    activeIndex: activeConversationIndex,
    lastIndex: lastConversationIndex,
  };
};

const openSnoozeModal = () => {
  const ninja = document.querySelector('ninja-keys');
  ninja.open({ parent: 'snooze_conversation' });
};

const toggleStatus = (status, snoozedUntil) => {
  closeDropdown();
  isLoading.value = true;
  store
    .dispatch('toggleStatus', {
      conversationId: currentChat.value.id,
      status,
      snoozedUntil,
    })
    .then(() => {
      useAlert(t('CONVERSATION.CHANGE_STATUS'));
      isLoading.value = false;
    });
};

const onCmdOpenConversation = () => {
  toggleStatus(wootConstants.STATUS_TYPE.OPEN);
};

const onCmdResolveConversation = () => {
  toggleStatus(wootConstants.STATUS_TYPE.RESOLVED);
};

const keyboardEvents = {
  'Alt+KeyM': {
    action: () => arrowDownButtonRef.value?.$el.click(),
    allowOnFocusedInput: true,
  },
  'Alt+KeyE': {
    action: async () => {
      await toggleStatus(wootConstants.STATUS_TYPE.RESOLVED);
    },
  },
  '$mod+Alt+KeyE': {
    action: async event => {
      const { all, activeIndex, lastIndex } = getConversationParams();
      await toggleStatus(wootConstants.STATUS_TYPE.RESOLVED);

      if (activeIndex < lastIndex) {
        all[activeIndex + 1].click();
      } else if (all.length > 1) {
        all[0].click();
        document.querySelector('.conversations-list').scrollTop = 0;
      }
      event.preventDefault();
    },
  },
};

useKeyboardEvents(keyboardEvents);

useEmitter(CMD_REOPEN_CONVERSATION, onCmdOpenConversation);
useEmitter(CMD_RESOLVE_CONVERSATION, onCmdResolveConversation);
</script>

<template>
  <div class="relative flex items-center justify-end resolve-actions gap-2">
    <!-- Botão Reabrir (quando conversa está fechada) -->
    <Button
      v-if="showReopenButton"
      :label="t('CONVERSATION.HEADER.REOPEN_ACTION')"
      size="sm"
      color="slate"
      :is-loading="isLoading"
      @click="onCmdOpenConversation"
    />
    
    <!-- Botão Dropdown Status da Conversa (sempre visível) -->
    <div class="rounded-lg shadow outline-1 outline outline-n-container flex-shrink-0">
      <Button
        ref="arrowDownButtonRef"
        :label="t('CONVERSATION.HEADER.CONVERSATION_STATUS')"
        icon="i-lucide-chevron-down"
        :disabled="isLoading"
        size="sm"
        color="slate"
        trailing-icon
        @click="openDropdown"
      />
    </div>
    <div
      v-if="showActionsDropdown"
      v-on-clickaway="closeDropdown"
      class="border rounded-lg shadow-lg border-n-strong dark:border-n-strong box-content p-2 w-fit z-10 bg-n-alpha-3 backdrop-blur-[100px] absolute block left-auto top-full mt-0.5 start-0 xl:start-auto xl:end-0 max-w-[12.5rem] min-w-[9.75rem] [&_ul>li]:mb-0"
    >
      <WootDropdownMenu class="mb-0">
        <WootDropdownItem v-if="!isResolved">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.MARK_RESOLVED')"
            ghost
            slate
            sm
            start
            icon="i-lucide-check"
            class="w-full"
            @click="() => toggleStatus(wootConstants.STATUS_TYPE.RESOLVED)"
          />
        </WootDropdownItem>
        <WootDropdownItem v-if="!isShowedInterest">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.MARK_SHOWED_INTEREST')"
            ghost
            slate
            sm
            start
            icon="i-lucide-sparkles"
            class="w-full"
            @click="() => toggleStatus(wootConstants.STATUS_TYPE.SHOWED_INTEREST)"
          />
        </WootDropdownItem>
        <WootDropdownItem v-if="!isVisitedStore">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.MARK_VISITED_STORE')"
            ghost
            slate
            sm
            start
            icon="i-lucide-store"
            class="w-full"
            @click="() => toggleStatus(wootConstants.STATUS_TYPE.VISITED_STORE)"
          />
        </WootDropdownItem>
        <WootDropdownItem v-if="!isPending">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.MARK_PENDING')"
            ghost
            slate
            sm
            start
            icon="i-lucide-circle-dot-dashed"
            class="w-full"
            @click="() => toggleStatus(wootConstants.STATUS_TYPE.PENDING)"
          />
        </WootDropdownItem>
        <WootDropdownItem v-if="!isPending">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.SNOOZE_UNTIL')"
            ghost
            slate
            sm
            start
            icon="i-lucide-alarm-clock-minus"
            class="w-full"
            @click="() => openSnoozeModal()"
          />
        </WootDropdownItem>
        <WootDropdownItem v-if="!isClosed">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.MARK_CLOSED')"
            ghost
            slate
            sm
            start
            icon="i-lucide-circle-x"
            class="w-full"
            @click="() => toggleStatus(wootConstants.STATUS_TYPE.CLOSED)"
          />
        </WootDropdownItem>
        <WootDropdownItem v-if="!isRejected">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.MARK_REJECTED')"
            ghost
            slate
            sm
            start
            icon="i-lucide-thumbs-down"
            class="w-full"
            @click="() => toggleStatus(wootConstants.STATUS_TYPE.REJECTED)"
          />
        </WootDropdownItem>
        <WootDropdownItem v-if="!isAbandoned">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.MARK_ABANDONED')"
            ghost
            slate
            sm
            start
            icon="i-lucide-user-x"
            class="w-full"
            @click="() => toggleStatus(wootConstants.STATUS_TYPE.ABANDONED)"
          />
        </WootDropdownItem>
      </WootDropdownMenu>
    </div>
  </div>
</template>
